import React, { useState, useEffect, useMemo } from "react";
import { Row, Col, Card, Spinner, Alert } from "react-bootstrap";
import {
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { FaListUl, FaArrowUp, FaArrowDown, FaBoxes, FaWarehouse } from "react-icons/fa";
import Breadcrumb from "app/components/Breadcrumb";
import axios from "app/services/api";

// ─── Helpers ────────────────────────────────────────────────────────────────
const formatCurrency = (value) => {
  const num = Number(value) || 0;
  return num.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatDateLabel = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return new Intl.DateTimeFormat("en-IN", { day: "2-digit", month: "short" }).format(d);
};

// ─── Stat Card ──────────────────────────────────────────────────────────────
const StatCard = ({ title, value, icon: Icon, bgColor, iconBg, loading }) => (
  <Card
    className="border-0 h-100"
    style={{
      borderRadius: "16px",
      boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
      transition: "transform 0.25s ease, box-shadow 0.25s ease",
      cursor: "default",
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.transform = "translateY(-6px)";
      e.currentTarget.style.boxShadow = "0 12px 32px rgba(0,0,0,0.12)";
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.transform = "translateY(0)";
      e.currentTarget.style.boxShadow = "0 4px 20px rgba(0,0,0,0.06)";
    }}
  >
    <Card.Body className="d-flex align-items-center gap-3 p-4">
      <div
        style={{
          width: 60,
          height: 60,
          borderRadius: "14px",
          background: iconBg,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <Icon size={28} color={bgColor} />
      </div>
      <div>
        <p
          className="mb-1 text-uppercase fw-bold"
          style={{ fontSize: "0.72rem", letterSpacing: "0.8px", color: "#6b7280" }}
        >
          {title}
        </p>
        {loading ? (
          <Spinner animation="border" size="sm" variant="secondary" />
        ) : (
          <h3 className="mb-0 fw-bold" style={{ color: "#1e293b", fontSize: "1.65rem" }}>
            {value}
          </h3>
        )}
      </div>
    </Card.Body>
  </Card>
);

// ─── Stock Value Card ───────────────────────────────────────────────────────
const StockValueCard = ({ value, loading }) => (
  <Card
    className="border-0"
    style={{
      borderRadius: "16px",
      boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
      background: "linear-gradient(135deg, #e0f2fe 0%, #f0f9ff 100%)",
      transition: "transform 0.25s ease, box-shadow 0.25s ease",
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.transform = "translateY(-6px)";
      e.currentTarget.style.boxShadow = "0 12px 32px rgba(0,0,0,0.12)";
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.transform = "translateY(0)";
      e.currentTarget.style.boxShadow = "0 4px 20px rgba(0,0,0,0.06)";
    }}
  >
    <Card.Body className="d-flex align-items-center gap-3 p-4">
      <div
        style={{
          width: 60,
          height: 60,
          borderRadius: "14px",
          background: "#0284c720",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <FaWarehouse size={28} color="#0284c7" />
      </div>
      <div>
        <p
          className="mb-1 text-uppercase fw-bold"
          style={{ fontSize: "0.72rem", letterSpacing: "0.8px", color: "#6b7280" }}
        >
          All Stock Value
        </p>
        {loading ? (
          <Spinner animation="border" size="sm" variant="secondary" />
        ) : (
          <h3 className="mb-0 fw-bold" style={{ color: "#0284c7", fontSize: "1.65rem" }}>
            ₹{formatCurrency(value)}
          </h3>
        )}
      </div>
    </Card.Body>
  </Card>
);

// ─── Chart Card wrapper ─────────────────────────────────────────────────────
const ChartCard = ({ title, subtitle, gradientFrom, gradientTo, children }) => (
  <Card
    className="border-0 h-100"
    style={{
      borderRadius: "16px",
      boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
      overflow: "hidden",
    }}
  >
    <div style={{ height: 5, background: `linear-gradient(90deg, ${gradientFrom}, ${gradientTo})` }} />
    <Card.Header className="border-0 bg-transparent px-4 pt-4 pb-2">
      <Card.Title as="h5" className="mb-0 fw-bold" style={{ fontSize: "1.05rem" }}>
        {title}
      </Card.Title>
      {subtitle && <p className="text-muted small mb-0 mt-1">{subtitle}</p>}
    </Card.Header>
    <Card.Body className="px-4 pb-4 pt-0">{children}</Card.Body>
  </Card>
);

// ─── Custom Tooltip ─────────────────────────────────────────────────────────
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #e2e8f0",
        borderRadius: 10,
        padding: "10px 14px",
        boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
      }}
    >
      <p className="mb-1 fw-semibold" style={{ fontSize: "0.85rem", color: "#334155" }}>
        {label}
      </p>
      {payload.map((entry, i) => (
        <p key={i} className="mb-0" style={{ fontSize: "0.8rem", color: entry.color }}>
          {entry.name}: <strong>{entry.value}</strong>
        </p>
      ))}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════
export default function ProductDashboard() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [data, setData] = useState({
    categoryCount: 0,
    inwardCount: 0,
    outwardCount: 0,
    lowStockCount: 0,
    totalStockValue: 0,
    dailyInwardOutward: [],
    monthlyInwardOutward: [],
  });

  useEffect(() => {
    let cancelled = false;

    setLoading(true);
    setError("");

    axios
      .get("/api/product-dashboard/summary")
      .then(({ data: resp }) => {
        if (cancelled) return;
        setData(resp);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("Product dashboard fetch error:", err);
        setError(err.response?.data?.message || "Failed to load dashboard data.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  // ── Prepare chart data ────────────────────────────────────────────────
  const dailyChartData = useMemo(
    () =>
      (data.dailyInwardOutward || []).map((row) => ({
        name: formatDateLabel(row.date),
        Inward: Number(row.inward_qty) || 0,
        Outward: Number(row.outward_qty) || 0,
      })),
    [data.dailyInwardOutward]
  );

  const monthlyChartData = useMemo(
    () =>
      (data.monthlyInwardOutward || []).map((row) => ({
        name: row.label || formatDateLabel(row.date),
        Inward: Number(row.inward_qty) || 0,
        Outward: Number(row.outward_qty) || 0,
      })),
    [data.monthlyInwardOutward]
  );

  const overviewChartData = useMemo(
    () => [
      { name: "All Category", Count: data.categoryCount || 0, fill: "#10b981" },
      { name: "All Inwardlist", Count: data.inwardCount || 0, fill: "#3b82f6" },
      { name: "All Outwardlist", Count: data.outwardCount || 0, fill: "#f59e0b" },
      { name: "Low Level Stock", Count: data.lowStockCount || 0, fill: "#ef4444" },
    ],
    [data.categoryCount, data.inwardCount, data.outwardCount, data.lowStockCount]
  );

  const routeSegments = [
    { name: "Product Management", path: "/product/products" },
    { name: "Dashboard" },
  ];

  return (
    <div
      className="pb-5 px-4"
      style={{
        backgroundColor: "#f8f9fa",
        minHeight: "100vh",
        borderRadius: "20px",
        paddingTop: "20px",
      }}
    >
      <Breadcrumb routeSegments={routeSegments} />

      {error && (
        <Alert variant="danger" className="mt-3" dismissible onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {/* ─── ROW 1: Stat Cards ───────────────────────────────────────── */}
      <Row className="g-4 mt-1">
        <Col xl={3} md={6}>
          <StatCard
            title="All Category"
            value={data.categoryCount}
            icon={FaListUl}
            bgColor="#3b82f6"
            iconBg="#3b82f620"
            loading={loading}
          />
        </Col>
        <Col xl={3} md={6}>
          <StatCard
            title="All Inwardlist"
            value={data.inwardCount}
            icon={FaBoxes}
            bgColor="#10b981"
            iconBg="#10b98120"
            loading={loading}
          />
        </Col>
        <Col xl={3} md={6}>
          <StatCard
            title="All Outwardlist"
            value={data.outwardCount}
            icon={FaArrowUp}
            bgColor="#f59e0b"
            iconBg="#f59e0b20"
            loading={loading}
          />
        </Col>
        <Col xl={3} md={6}>
          <StatCard
            title="Low Level Stock"
            value={data.lowStockCount}
            icon={FaArrowDown}
            bgColor="#ef4444"
            iconBg="#ef444420"
            loading={loading}
          />
        </Col>
      </Row>

      {/* ─── ROW 2: Stock Value ──────────────────────────────────────── */}
      <Row className="g-4 mt-1">
        <Col md={6} lg={4}>
          <StockValueCard value={data.totalStockValue} loading={loading} />
        </Col>
      </Row>

      {/* ─── ROW 3: Daily & Monthly Charts ───────────────────────────── */}
      <Row className="g-4 mt-1">
        <Col lg={6}>
          <ChartCard
            title="Daily Inward vs Outward"
            subtitle="Last 7 days quantity comparison"
            gradientFrom="#3b82f6"
            gradientTo="#10b981"
          >
            {loading ? (
              <div className="py-5 text-center text-muted">
                <Spinner animation="border" size="sm" className="me-2" />
                Loading chart…
              </div>
            ) : dailyChartData.length === 0 ? (
              <Alert variant="info" className="mb-0">
                No daily data available.
              </Alert>
            ) : (
              <div style={{ width: "100%", height: 340 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={dailyChartData} barSize={20} margin={{ top: 10, right: 10, left: 0, bottom: 30 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} angle={-30} textAnchor="end" height={60} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend wrapperStyle={{ paddingTop: 10 }} />
                    <Bar dataKey="Inward" fill="#10b981" radius={[6, 6, 0, 0]} />
                    <Bar dataKey="Outward" fill="#3b82f6" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </ChartCard>
        </Col>

        <Col lg={6}>
          <ChartCard
            title="Monthly Inward vs Outward"
            subtitle="Last 6 months quantity comparison"
            gradientFrom="#8b5cf6"
            gradientTo="#ec4899"
          >
            {loading ? (
              <div className="py-5 text-center text-muted">
                <Spinner animation="border" size="sm" className="me-2" />
                Loading chart…
              </div>
            ) : monthlyChartData.length === 0 ? (
              <Alert variant="info" className="mb-0">
                No monthly data available.
              </Alert>
            ) : (
              <div style={{ width: "100%", height: 340 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={monthlyChartData} barSize={28} margin={{ top: 10, right: 10, left: 0, bottom: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend wrapperStyle={{ paddingTop: 10 }} />
                    <Bar dataKey="Inward" fill="#10b981" radius={[6, 6, 0, 0]} />
                    <Bar dataKey="Outward" fill="#f59e0b" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </ChartCard>
        </Col>
      </Row>

      {/* ─── ROW 4: Overview Bar Chart ───────────────────────────────── */}
      <Row className="g-4 mt-1">
        <Col xs={12}>
          <ChartCard
            title="Overview"
            subtitle="Summary count across all categories, inward, outward, and low stock"
            gradientFrom="#f59e0b"
            gradientTo="#ef4444"
          >
            {loading ? (
              <div className="py-5 text-center text-muted">
                <Spinner animation="border" size="sm" className="me-2" />
                Loading chart…
              </div>
            ) : (
              <div style={{ width: "100%", height: 380 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={overviewChartData} barSize={60} margin={{ top: 20, right: 30, left: 0, bottom: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend wrapperStyle={{ paddingTop: 10 }} />
                    <Bar dataKey="Count" radius={[8, 8, 0, 0]}>
                      {overviewChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </ChartCard>
        </Col>
      </Row>

      {/* ─── Footer ──────────────────────────────────────────────────── */}
      <Row>
        <div className="col-12 mt-4 text-center">
          <p className="text-muted small">Product Management Dashboard • Version 1.0</p>
        </div>
      </Row>
    </div>
  );
}
